import { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api as http, getGatewayUploadsUrl } from '../services/api';

// Épingle rouge (SVG) — évite l'icône Leaflet par défaut (assets cassés en build).
const RED_PIN = L.divIcon({
  className: '',
  html: '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 24 34"><path fill="#dc2626" stroke="#fff" stroke-width="1.5" d="M12 1C6.5 1 2 5.5 2 11c0 7 10 22 10 22s10-15 10-22C22 5.5 17.5 1 12 1z"/><circle cx="12" cy="11" r="4" fill="#fff"/></svg>',
  iconSize: [28, 40],
  iconAnchor: [14, 40],
});

// Détail d'une mission : infos client + remarques/options de l'état des lieux, avertissement
// (« Attention ! » au-dessus des commentaires), et commentaires avec pièces jointes.
const TYPES = { pickup: 'Pick up', livraison: 'Livraison', installation: 'Installation', desinstallation: 'Désinstallation', retour: 'Retour' };
const STATUTS = { a_venir: 'À venir', en_cours: 'En cours', termine: 'Terminé', valide: 'Validé', annule: 'Annulé' };
const SENS = { depart: 'Départ', retour: 'Retour' };
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '');
const COLORS = ['#e11d6f', '#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2'];

function Avatar({ name, size = 30, glow, photo }) {
  const initials = (name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
  const idx = [...(name || '?')].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length;
  const common = { width: size, height: size, borderRadius: size / 2, flexShrink: 0, boxShadow: glow ? '0 0 0 3px rgba(220,38,38,.5)' : 'none' };
  if (photo) {
    return <img src={photo} alt={name || ''} style={{ ...common, objectFit: 'cover', background: '#e2e8f0' }} />;
  }
  return (
    <div style={{ ...common, background: COLORS[idx], color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 700 }}>
      {initials}
    </div>
  );
}

const fileToDataURL = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });

export default function MissionDetailModal({ mission, missionsUrl, token, onClose, onChanged }) {
  const [comments, setComments] = useState([]);
  const [warning, setWarning] = useState(mission.warning || null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [warnModal, setWarnModal] = useState(false);
  const [motif, setMotif] = useState('');
  const [avatars, setAvatars] = useState({}); // user_id -> URL avatar (photo du compte)

  const api = useCallback((path, opts = {}) => fetch(`${missionsUrl}/api/missions/${mission.id}${path}`, {
    ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  }), [missionsUrl, mission.id, token]);

  const load = useCallback(() => {
    api('/comments').then((r) => r.json()).then((d) => setComments(d.data || [])).catch(() => {});
    api('/warning').then((r) => r.json()).then((d) => setWarning(d.data || null)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // Résout les avatars (photo du compte) des auteurs de commentaires + de l'avertissement.
  useEffect(() => {
    const ids = [...new Set([...comments.map((c) => c.author_user_id), warning?.by_user_id].filter((v) => Number.isInteger(v)))];
    const missing = ids.filter((id) => !(id in avatars));
    if (missing.length === 0) return;
    http.get(`/users/avatars?ids=${missing.join(',')}`)
      .then((res) => {
        const map = {};
        const data = res.data?.data || {};
        for (const id of missing) map[id] = data[id]?.user_photo ? getGatewayUploadsUrl(data[id].user_photo) : null;
        setAvatars((prev) => ({ ...prev, ...map }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, warning]);

  const onPickFiles = async (e) => {
    const list = Array.from(e.target.files || []); e.target.value = '';
    try {
      const out = [];
      for (const f of list) {
        if (f.size > 6 * 1024 * 1024) { setErr(`${f.name} : trop volumineux (max 6 Mo).`); continue; }
        out.push({ name: f.name, mime: f.type, data: await fileToDataURL(f) });
      }
      setFiles((prev) => [...prev, ...out]);
    } catch { setErr('Lecture du fichier impossible.'); }
  };

  const addComment = async () => {
    if (!body.trim() && !files.length) return;
    setBusy(true); setErr(null);
    try {
      const r = await api('/comments', { method: 'POST', body: JSON.stringify({ body, files }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || ('HTTP ' + r.status));
      setBody(''); setFiles([]); load(); // liste mise à jour, popup conservée
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const saveWarning = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api('/warning', { method: 'POST', body: JSON.stringify({ motif }) });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setWarnModal(false); setMotif(''); load(); onChanged && onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const clearWarning = async () => {
    setBusy(true);
    try { await api('/warning', { method: 'DELETE' }); load(); onChanged && onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const inp = { width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, fontFamily: 'inherit' };
  const addr = [mission.client_adresse, mission.client_cp, mission.client_ville].filter(Boolean).join(', ');
  const la = parseFloat(mission.client_lat); const lo = parseFloat(mission.client_lng);
  const hasCoords = Number.isFinite(la) && Number.isFinite(lo) && (la !== 0 || lo !== 0);
  const etatsText = (mission.etats || []).filter((e) => (e.remarques || '').trim() || (e.options || '').trim());
  const missionDate = mission.scheduled_at || mission.created_at;
  const canComment = !!(body.trim() || files.length);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', background: 'white', borderRadius: 14, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            <h3 style={{ margin: 0 }}>{mission.titre || TYPES[mission.mission_type]}</h3>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
              {TYPES[mission.mission_type] || mission.mission_type} · {STATUTS[mission.statut] || mission.statut}
              {mission.borne?.borne_n ? ` · ${mission.borne.borne_type || ''} ${mission.borne.borne_n}` : ''}
            </div>
            {missionDate && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>📅 {fmtDateTime(missionDate)}</div>}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 24, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>&times;</button>
        </div>

        {/* Client */}
        {(mission.client_ref || addr) && (
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 12px', margin: '8px 0', fontSize: 14 }}>
            <b>{mission.client_ref || '—'}</b>{addr && <div style={{ color: '#475569', marginTop: 2 }}>📍 {addr}</div>}
            {hasCoords && (
              <div style={{ marginTop: 8, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <MapContainer center={[la, lo]} zoom={15} style={{ height: 170, width: '100%' }} scrollWheelZoom={false} attributionControl={false}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[la, lo]} icon={RED_PIN} />
                </MapContainer>
              </div>
            )}
          </div>
        )}

        {/* Remarques / options de l'état des lieux */}
        {etatsText.length > 0 && (
          <div style={{ margin: '8px 0' }}>
            {etatsText.map((e, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 12px', marginBottom: 8, fontSize: 13.5 }}>
                <div style={{ fontWeight: 700, color: '#334155', marginBottom: 4 }}>État des lieux — {SENS[e.sens] || e.sens}</div>
                {(e.remarques || '').trim() && <div><span style={{ color: '#64748b' }}>Remarques :</span> {e.remarques}</div>}
                {(e.options || '').trim() && <div style={{ marginTop: 2 }}><span style={{ color: '#64748b' }}>Options supplémentaires :</span> {e.options}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Avertissement en évidence au-dessus des commentaires */}
        {warning && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 12, margin: '10px 0' }}>
            <Avatar name={warning.by_nom} glow photo={avatars[warning.by_user_id]} />
            <div style={{ flex: 1 }}>
              <div style={{ color: '#b91c1c', fontWeight: 800, fontSize: 14 }}>Attention !</div>
              <div style={{ color: '#7f1d1d', fontSize: 12.5, marginTop: 1 }}>{warning.by_nom ? `par ${warning.by_nom}` : ''}{warning.created_at ? ` · ${fmtDateTime(warning.created_at)}` : ''}</div>
              {warning.motif && <div style={{ marginTop: 5, color: '#7f1d1d' }}>{warning.motif}</div>}
              <button onClick={clearWarning} disabled={busy} style={{ marginTop: 8, padding: '4px 10px', borderRadius: 7, border: '1px solid #fecaca', background: 'white', color: '#b91c1c', fontSize: 12, cursor: 'pointer' }}>Lever l'avertissement</button>
            </div>
          </div>
        )}

        {/* Commentaires */}
        <h4 style={{ margin: '14px 0 8px' }}>Commentaires ({comments.length})</h4>
        {comments.length === 0 && <p style={{ color: '#94a3b8', fontSize: 13 }}>Aucun commentaire.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {comments.map((c) => (
            <div key={c.id} style={{ display: 'flex', gap: 10 }}>
              <Avatar name={c.author_nom} size={32} photo={avatars[c.author_user_id]} />
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '8px 12px' }}>
                <div style={{ fontSize: 12.5, color: '#64748b' }}><b style={{ color: '#334155' }}>{c.author_nom || 'Utilisateur'}</b> · {fmtDateTime(c.created_at)}</div>
                {c.body && <div style={{ fontSize: 14, marginTop: 3, whiteSpace: 'pre-wrap' }}>{c.body}</div>}
                {Array.isArray(c.files) && c.files.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {c.files.map((f, i) => (f.mime?.startsWith('image/')
                      ? <a key={i} href={f.data} target="_blank" rel="noreferrer"><img src={f.data} alt={f.name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} /></a>
                      : <a key={i} href={f.data} download={f.name} style={{ fontSize: 12, color: '#2563eb', textDecoration: 'underline' }}>📎 {f.name}</a>))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Zone commentaire + actions */}
        <div style={{ marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Ajouter un commentaire…" style={inp} />
          {files.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {files.map((f, i) => <span key={i} style={{ fontSize: 12, background: '#eef2f7', padding: '3px 8px', borderRadius: 6 }}>📎 {f.name} <button onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626' }}>×</button></span>)}
            </div>
          )}
          {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '6px 0 0' }}>{err}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontSize: 13 }}>
              📎 Joindre<input type="file" multiple onChange={onPickFiles} style={{ display: 'none' }} />
            </label>
            {!warning && (
              <button data-perm="antennes:contacts.missions_comment" onClick={() => setWarnModal(true)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontWeight: 600, cursor: 'pointer' }}>⚠️ Marquer un avertissement</button>
            )}
            {canComment && (
              <button data-perm="antennes:contacts.missions_comment" onClick={addComment} disabled={busy} style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, border: 'none', background: '#e11d6f', color: 'white', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>Enregistrer le commentaire</button>
            )}
          </div>
        </div>
      </div>

      {/* 2e popup : marquer un avertissement */}
      {warnModal && (
        <div onClick={(e) => { e.stopPropagation(); setWarnModal(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: 'white', borderRadius: 14, padding: 22 }}>
            <h3 style={{ margin: '0 0 6px', color: '#b91c1c' }}>⚠️ Marquer un avertissement</h3>
            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 14 }}>Marquer cette mission : les autres utilisateurs verront vos remarques la concernant.</p>
            <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} placeholder="Remarque / motif de l'avertissement…" style={inp} autoFocus />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button onClick={() => setWarnModal(false)} style={{ flex: 1, padding: 11, borderRadius: 10, border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
              <button onClick={saveWarning} disabled={busy || !motif.trim()} style={{ flex: 1, padding: 11, borderRadius: 10, border: 'none', background: '#dc2626', color: 'white', fontWeight: 700, cursor: 'pointer', opacity: busy || !motif.trim() ? 0.6 : 1 }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
